// Reads the runtime-injected `window._env_` that is written either by
// `public/env.js` (local dev) or by `entrypoint.sh` inside the Docker container.

function readEnv<K extends keyof Window['_env_']>(key: K): string {
  if (typeof window === 'undefined') return ''
  return window._env_?.[key] ?? ''
}

export const isDev = (): boolean => readEnv('DEVELOPMENT') === '1'
export const isStaging = (): boolean => readEnv('STAGING') === '1'
export const isProd = (): boolean => !isDev() && !isStaging()

export const backendHostForPage = (configuredHost: string, pageHostname: string): string => {
  if (pageHostname === 'cortex.migraine.org') return 'api.migraine.org'
  if (pageHostname === 'staging.cortex.migraine.org') return 'staging.api.migraine.org'
  return configuredHost
}

export const patientPortalHostForPage = (configuredHost: string, pageHostname: string): string => {
  if (pageHostname === 'cortex.migraine.org') return 'app.migraine.org'
  if (pageHostname === 'staging.cortex.migraine.org') return 'staging.app.migraine.org'
  return configuredHost
}

export const domainHandoffUrl = (pageHostname: string, next: string): string | undefined => {
  const legacyApiHosts: Record<string, string> = {
    'staff.themigraineclinic.org': 'api.themigraineclinic.org',
    'staging.staff.themigraineclinic.org': 'staging.api.themigraineclinic.org',
  }
  const apiHost = legacyApiHosts[pageHostname]
  if (!apiHost) return undefined
  return `https://${apiHost}/auth/domainHandoff?portal=employee&next=${encodeURIComponent(next)}`
}

export const backendUrl = (): string => {
  const configuredHost = readEnv('BACKEND_HOST') || 'localhost'
  // Keep API requests same-site so SameSite=Strict auth cookies work on both
  // the current and new domain families.
  const host = typeof window === 'undefined'
    ? configuredHost
    : backendHostForPage(configuredHost, window.location.hostname)
  const port = readEnv('BACKEND_PORT') || '8000'
  const ssl = readEnv('BACKEND_SSL') === '1'
  const scheme = ssl ? 'https' : 'http'
  return `${scheme}://${host}:${port}`
}

export const patientPortalHost = (): string => {
  const configuredHost = readEnv('PATIENT_PORTAL_HOST')
    || (isDev() ? 'patient.tmc' : isStaging() ? 'staging.patient.themigraineclinic.org' : 'patient.themigraineclinic.org')
  return typeof window === 'undefined'
    ? configuredHost
    : patientPortalHostForPage(configuredHost, window.location.hostname)
}
